// ============================================
// HLX-PRO.08 — Gate Classification Types
//
// Binary gate system: Gate 1 (OTC checkout) or
// Gate 2 (provider review). No gradient, no bypass.
//
// Gate 1 must be EARNED by passing all 7 criteria.
// Gate 2 is the natural/default state.
//
// LOCKED CONSTRAINTS:
// - Any single trigger → Gate 2
// - Pregnancy ALWAYS triggers Gate 2
// - Classification error → Gate 2 (never Gate 1)
// - Tier 2/3 products → Gate 2 (always)
// - Conservative defaults → Gate 2 (always)
// ============================================

import type { BiomarkerResult, Protocol } from './index';
import type { SafetyRuleSeverity } from './formulary';

// ── Gate Assignment ──

export type Gate = 'GATE_1' | 'GATE_2';

// ── Trigger Types ──

export type GateTriggerType =
  | 'MEDICATION_COUNT'       // 2+ medications
  | 'HIGH_RISK_MEDICATION'   // Anticoagulants, immunosuppressants, etc.
  | 'PREGNANCY'              // isPregnant === true
  | 'BREASTFEEDING'          // isBreastfeeding === true
  | 'BIOMARKER_DEVIATION'    // Any biomarker >2 SD from optimal range
  | 'CONTRAINDICATION_RULE'  // Any block-level safety rule triggered
  | 'PRODUCT_TIER'           // Any Tier 2 (Rx) or Tier 3 (Longevity) product
  | 'CONSERVATIVE_DEFAULT'   // Conservative defaults were applied (missing data)
  | 'CLASSIFICATION_ERROR';  // Error during classification → auto Gate 2

export interface GateTrigger {
  /** Which criterion triggered Gate 2 */
  type: GateTriggerType;
  /** Human-readable detail about what specifically triggered it */
  detail: string;
  /** The source data field that caused the trigger */
  sourceField: string;
  /** Severity weight for audit prioritization (not used for gate decision) */
  auditPriority: 'HIGH' | 'MEDIUM';
}

// ── Approval Status ──

export type ApprovalStatus =
  | 'GATE_1_APPROVED'           // Gate 1: no provider review needed
  | 'PENDING_PROVIDER_REVIEW'   // Gate 2: waiting for provider
  | 'PROVIDER_APPROVED'         // Gate 2: provider approved
  | 'PROVIDER_REJECTED'         // Gate 2: provider rejected
  | 'PROVIDER_MODIFIED'         // Gate 2: provider modified, pending re-validation
  | 'REVALIDATION_FAILED';      // Gate 2: provider modification failed PRO.07

// ── Biomarker Deviation ──

export interface BiomarkerDeviation {
  biomarkerName: string;
  patientValue: number;
  unit: string;
  optimalLow: number;
  optimalHigh: number;
  sdFromOptimal: number;
  direction: 'above' | 'below';
}

// ── Filter Exclusion (from PRO.04 audit) ──

export interface FilterExclusion {
  ruleId: string;
  productId: string;
  productName: string;
  severity: SafetyRuleSeverity;
  reason: string;
}

// ── Provider Clinical Summary ──

export interface ProviderClinicalSummary {
  patientProfile: {
    sex: 'female' | 'male' | 'unknown';
    age: number | null;
    medications: string[];
    allergies: string[];
    conditions: string[];
    isPregnant: boolean | null;
    isBreastfeeding: boolean | null;
  };
  biomarkerDeviations: BiomarkerDeviation[];
  preFilterExclusions: FilterExclusion[];
  postValidationWarnings: string[];
  triggers: GateTrigger[];
  protocolItems: ProtocolItemSummary[];
}

export interface ProtocolItemSummary {
  productName: string;
  dosage: string;
  timing: string;
  rationale: string;
  tier: number;
  requiresRx: boolean;
}

// ── Gate Classified Protocol (main output) ──

export interface GateClassifiedProtocol {
  /** The protocol, unchanged */
  protocol: Protocol;
  /** Binary gate assignment */
  gate: Gate;
  /** Every trigger that fired. Empty array = Gate 1. */
  triggers: GateTrigger[];
  /** Human-readable reasoning */
  reasoning: string;
  /** Provider summary. Only populated for Gate 2. */
  providerSummary: ProviderClinicalSummary | null;
  /** ISO 8601 classification timestamp */
  classifiedAt: string;
  /** Unique ID for audit linking */
  classificationId: string;
  /** Current approval status */
  approvalStatus: ApprovalStatus;
}

// ── Classification Input ──

export interface GateClassificationInput {
  /** The protocol to classify */
  protocol: Protocol;
  /** Patient safety profile */
  patientProfile: {
    sex?: 'female' | 'male';
    age?: number;
    isPregnant?: boolean;
    isBreastfeeding?: boolean;
    medications: string[];
    allergies: string[];
    conditions: string[];
  };
  /** Normalized medication RxCUI IDs for contraindication checking (R2 — PRO.02) */
  medicationRxCuis?: Map<string, RxCUI>;
  /** Biomarker results with optimal ranges */
  biomarkerResults: BiomarkerResult[];
  /** Safety rule violations from PRO.04/PRO.07 */
  safetyViolations: SafetyViolationInput[];
  /** Pre-filter exclusions from PRO.04 */
  preFilterExclusions: FilterExclusion[];
  /** Post-validation warnings from PRO.07 */
  postValidationWarnings: string[];
  /** Whether conservative defaults were applied */
  conservativeDefaultsApplied: boolean;
  /** Which fields had conservative defaults */
  conservativeDefaultFields: string[];
  /** Product details for tier checking */
  productDetails: ProductDetailInput[];
}

export interface SafetyViolationInput {
  ruleId: string;
  ruleName: string;
  severity: SafetyRuleSeverity;
  affectedProducts: string[];
  message: string;
}

export interface ProductDetailInput {
  productId: string;
  name: string;
  tier: number;
  requiresRx: boolean;
}

// ── High-Risk Medication Types ──

/** RxNorm Concept Unique Identifier */
export type RxCUI = string;

export interface HighRiskMedicationMatch {
  isHighRisk: boolean;
  medication: string;
  drugClass?: string;
  rxCui?: RxCUI;
}

// ── Non-Normal Biomarkers ──

/**
 * Biomarkers with non-normal distributions that should use
 * range-based fallback instead of SD calculation.
 * Clinical team validation required before switching to SD.
 */
export const NON_NORMAL_BIOMARKERS: Set<string> = new Set([
  'ferritin',
  'tsh',
  'thyroid stimulating hormone',
  'vitamin d',
  '25-hydroxyvitamin d',
]);
